// Multi-select label picker — Monday-style colored chips.
//
// Trigger renders the current labels as colored pills (max 4 inline, "+N" for
// the rest) plus an "+ Add" affordance. Click opens a dropdown that:
//   1) fetches /api/projects/:id/labels (cached per-projectId in module scope)
//   2) renders a checkbox row per label
//   3) shows an inline "Neue Label erstellen" composer with COLOR_PRESETS
//
// Selecting a label issues PUT /api/tasks/:id/labels with the full id-set.
// Creating a new label POSTs to /api/projects/:id/labels then auto-selects it.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import { createPortal } from "preact/compat";
import { Plus, Tag, X } from "lucide-preact";
import type { LabelItem, Task } from "@juuplan/shared";
import { createLabel, fetchLabels, setTaskLabels } from "../store/project";
import { addLabelToCache } from "../store/labels";
import { COLOR_PRESETS } from "../lib/format";
import { pickFg } from "./LabelChip";
import { toastError } from "../lib/toast";

import { Button } from "./ui/Button";

interface Props {
  task: Task;
  projectId: number;
}

// Module-scope cache so the same labels survive open/close cycles and view
// switches. Each projectId has its own bucket. Invalidated on createLabel().
// LRU-capped (see CACHE_MAX_PROJECTS) so long sessions that visit many
// projects don't leak memory forever (#77).
const cache = new Map<number, LabelItem[]>();

// Cap on distinct projects held in `cache`. 10 comfortably covers a normal
// working set (open tabs + recents) without letting the cache grow unbounded
// over a long-lived session.
const CACHE_MAX_PROJECTS = 10;

// Fallback swatch for label_ids whose details haven't loaded yet (see
// `missingIds` below). Reuses the existing "outline (neutral)" preset instead
// of a fresh hardcoded hex — that literal is already the app's one neutral
// label color (also used as the fallback in TaskModal/SpacesSection).
const FALLBACK_LABEL_COLOR = COLOR_PRESETS[COLOR_PRESETS.length - 1];

// Read that also marks `projectId` as most-recently-used (Map iteration order
// = insertion order, so re-inserting moves it to the end / "newest"). Has a
// side effect — only call this from effect/event paths, never from a render
// body or a useState initializer.
function cacheGet(projectId: number): LabelItem[] | undefined {
  const rows = cache.get(projectId);
  if (rows !== undefined) {
    cache.delete(projectId);
    cache.set(projectId, rows);
  }
  return rows;
}

// Write that also marks `projectId` as most-recently-used, then evicts the
// least-recently-used entry (the first key in iteration order) if the cache
// grew past CACHE_MAX_PROJECTS.
function cacheSet(projectId: number, rows: LabelItem[]): void {
  cache.delete(projectId);
  cache.set(projectId, rows);
  if (cache.size > CACHE_MAX_PROJECTS) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
}

export function LabelPicker(props: Props): preact.JSX.Element {
  const { task, projectId } = props;
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; placeAbove: boolean; maxH: number }>({
    top: 0,
    left: 0,
    placeAbove: false,
    maxH: 320,
  });
  // Side-effect-free read: this initializer runs during render, so it must
  // NOT use cacheGet (which mutates insertion order for the LRU).
  const [labels, setLabels] = useState<LabelItem[]>(
    () => cache.get(projectId) ?? []
  );
  const [loading, setLoading] = useState(false);
  // Getrennt von `labels`: ein fehlgeschlagenes GET (api() toastet nur
  // Schreibfehler, siehe lib/api.ts) hinterließ `labels` bisher als leeres
  // Array — optisch nicht von "dieses Projekt hat noch keine Labels" zu
  // unterscheiden.
  const [loadFailed, setLoadFailed] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<string>(COLOR_PRESETS[0]);
  const [creating, setCreating] = useState(false);

  // Lazy-load labels the first time the picker is opened for this project, or
  // whenever the projectId changes after that.
  useEffect(() => {
    if (!open) return;
    const cached = cacheGet(projectId);
    if (cached) {
      setLabels(cached);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    void fetchLabels(projectId).then(
      (rows) => {
        if (cancelled) return;
        cacheSet(projectId, rows);
        setLabels(rows);
        setLoading(false);
      },
      () => {
        if (cancelled) return;
        setLoading(false);
        setLoadFailed(true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  // Portal-position the dropdown against the trigger and clamp to the viewport
  // so the TaskModal's overflow-auto scroll container can't clip it and it never
  // runs off-screen on a narrow phone. Recomputes on scroll/resize.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const recompute = (): void => {
      const r = triggerRef.current!.getBoundingClientRect();
      const menuH = 320; // matches max-h-80 below
      const gap = 4;
      const margin = 8;
      const menuW = 288; // w-72
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const placeAbove = spaceBelow < menuH && spaceAbove > spaceBelow;
      const avail = (placeAbove ? spaceAbove : spaceBelow) - gap - margin;
      setMenuPos({
        top: placeAbove ? r.top - gap : r.bottom + gap,
        left: Math.max(margin, Math.min(r.left, window.innerWidth - menuW - margin)),
        placeAbove,
        maxH: Math.max(200, Math.min(menuH, avail)),
      });
    };
    recompute();
    window.addEventListener("scroll", recompute, true);
    window.addEventListener("resize", recompute);
    return () => {
      window.removeEventListener("scroll", recompute, true);
      window.removeEventListener("resize", recompute);
    };
  }, [open]);

  const byId = useMemo(() => {
    const m = new Map<number, LabelItem>();
    for (const l of labels) m.set(l.id, l);
    return m;
  }, [labels]);

  const selected = task.label_ids
    .map((id) => byId.get(id))
    .filter((l): l is LabelItem => l !== undefined);

  // Tasks may carry label_ids whose details we haven't fetched yet (compact
  // views). Render those as ghosted "Label #id" chips so we never show stale
  // names.
  const missingIds = task.label_ids.filter((id) => !byId.has(id));

  const toggle = async (labelId: number): Promise<void> => {
    const next = task.label_ids.includes(labelId)
      ? task.label_ids.filter((id) => id !== labelId)
      : [...task.label_ids, labelId];
    try {
      await setTaskLabels(task.id, next);
    } catch {
      // api wrapper toasted
    }
  };

  const submitNew = async (): Promise<void> => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const created = await createLabel(projectId, { name, color: newColor });
      const nextLabels = [...labels, created];
      cacheSet(projectId, nextLabels);
      setLabels(nextLabels);
      // Push into the global label store so TableView's Labels column +
      // GanttView's tooltip pick up the new label without a page reload.
      addLabelToCache(projectId, created);
      setNewName("");
      // Auto-select the new label on the current task.
      await setTaskLabels(task.id, [...task.label_ids, created.id]);
    } catch {
      // api wrapper toasted
    } finally {
      setCreating(false);
    }
  };

  return (
    <div class="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        class="w-full inline-flex flex-wrap items-center gap-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px] text-left"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Labels"
      >
        {selected.length === 0 && missingIds.length === 0 ? (
          <span class="inline-flex items-center gap-1 text-[color:var(--color-on-surface-variant)]">
            <Tag size={14} aria-hidden="true" />
            — Keine Labels —
          </span>
        ) : (
          <>
            {selected.slice(0, 4).map((l) => (
              <LabelChip key={l.id} name={l.name} color={l.color} />
            ))}
            {missingIds.slice(0, Math.max(0, 4 - selected.length)).map((id) => (
              <LabelChip key={`m-${id}`} name={`#${id}`} color={FALLBACK_LABEL_COLOR} />
            ))}
            {selected.length + missingIds.length > 4 && (
              <span class="text-label text-[color:var(--color-on-surface-variant)]">
                +{selected.length + missingIds.length - 4}
              </span>
            )}
          </>
        )}
        <span class="ml-auto inline-flex items-center gap-1 text-[color:var(--color-on-surface-variant)]">
          <Plus size={14} aria-hidden="true" />
          Add
        </span>
      </button>

      {open &&
        createPortal(
          <>
          <div
            class="fixed inset-0 z-[60]"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            role="listbox"
            aria-multiselectable="true"
            class="fixed z-[70] w-72 overflow-auto rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-2 py-1 text-xs"
            style={{
              top: `${menuPos.top}px`,
              left: `${menuPos.left}px`,
              maxHeight: `${menuPos.maxH}px`,
              transform: menuPos.placeAbove ? "translateY(-100%)" : "translateY(0)",
            }}
          >
            {loading && labels.length === 0 ? (
              <div class="px-3 py-2 text-[color:var(--color-on-surface-variant)]">
                Lädt…
              </div>
            ) : loadFailed && labels.length === 0 ? (
              <div class="px-3 py-2 text-[color:var(--color-error)]">
                Labels konnten nicht geladen werden.
              </div>
            ) : labels.length === 0 ? (
              <div class="px-3 py-2 text-[color:var(--color-on-surface-variant)] italic">
                Noch keine Labels — leg unten ein neues an.
              </div>
            ) : (
              labels.map((l) => {
                const checked = task.label_ids.includes(l.id);
                return (
                  <label
                    key={l.id}
                    class="flex items-center gap-2 px-3 py-1.5 hover:bg-[color:var(--color-surface-container-low)] cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => void toggle(l.id)}
                      class="accent-[color:var(--color-primary)]"
                    />
                    <span
                      class="inline-block size-3 rounded-full shrink-0"
                      style={{ background: l.color }}
                      aria-hidden="true"
                    />
                    <span class="truncate text-[color:var(--color-on-surface)]">
                      {l.name}
                    </span>
                  </label>
                );
              })
            )}

            <div class="border-t border-[color:var(--color-outline-variant)] mt-1 pt-2 px-3 pb-2 flex flex-col gap-1.5">
              <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                Neue Label erstellen
              </div>
              <input
                type="text"
                value={newName}
                onInput={(e) =>
                  setNewName((e.target as HTMLInputElement).value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void submitNew();
                  }
                }}
                placeholder="Label-Name"
                maxLength={60}
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                aria-label="Name des neuen Labels"
              />
              <div class="flex flex-wrap gap-1">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewColor(c)}
                    class={`size-5 rounded-full border-2 transition-transform ${
                      newColor === c
                        ? "border-[color:var(--color-on-surface)] scale-110"
                        : "border-transparent hover:scale-110"
                    }`}
                    style={{ background: c }}
                    aria-label={`Farbe ${c} wählen`}
                    aria-pressed={newColor === c}
                  />
                ))}
              </div>
              <Button
                variant="primary"
                size="xs"
                class="self-end"
                type="button"
                onClick={() => void submitNew()}
                disabled={!newName.trim() || creating}
              >
                {creating ? "Lege an…" : "Erstellen"}
              </Button>
            </div>
          </div>
          </>,
          document.body
        )}
    </div>
  );
}

function LabelChip(props: { name: string; color: string }): preact.JSX.Element {
  return (
    <span
      class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-label font-medium max-w-[10rem] truncate"
      style={{ background: props.color, color: pickFg(props.color) }}
      title={props.name}
    >
      {props.name}
    </span>
  );
}

/** Visual-only chip helper for read-only contexts (TaskRow, TaskCard). */
export function LabelChipReadonly(props: {
  name: string;
  color: string;
  onRemove?: () => void;
}): preact.JSX.Element {
  return (
    <span
      class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-label font-medium"
      style={{ background: props.color, color: pickFg(props.color) }}
    >
      {props.name}
      {props.onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            props.onRemove?.();
          }}
          class="rounded-full p-0.5 hover:bg-current/20"
          aria-label={`Label „${props.name}" entfernen`}
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

/** Helper to clear the label cache when a project is mutated externally. */
export function invalidateLabelCache(projectId?: number): void {
  if (projectId === undefined) cache.clear();
  else cache.delete(projectId);
}

// Test-only probes for the module-scope LRU cache (#77). Expose the narrow
// operations needed to verify eviction behavior without leaking the Map
// itself — do not use these outside tests.
export function __cacheSetForTest(projectId: number, rows: LabelItem[]): void {
  cacheSet(projectId, rows);
}
export function __cacheGetForTest(projectId: number): LabelItem[] | undefined {
  return cacheGet(projectId);
}
export function __cacheSizeForTest(): number {
  return cache.size;
}

/** Optional helper for error surfacing from siblings. */
export function showLabelError(msg: string): void {
  toastError(msg);
}
